import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Building2, Plus, ArrowDownToLine, ArrowUpFromLine, Check, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const Banking = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('accounts');
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Mock connected accounts
  const [connectedAccounts, setConnectedAccounts] = useState([
    {
      id: 1,
      bankName: 'Commonwealth Bank',
      accountNumber: '****6789',
      accountName: 'Savings Account',
      isPrimary: true
    }
  ]);

  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(null);

  const handleConnectBank = () => {
    setIsProcessing(true);
    // Simulate bank connection via Plaid/Basiq
    setTimeout(() => {
      setConnectedAccounts([...connectedAccounts, {
        id: connectedAccounts.length + 1,
        bankName: 'NAB',
        accountNumber: '****1234',
        accountName: 'Transaction Account',
        isPrimary: false
      }]);
      setIsProcessing(false);
      setShowConnectModal(false);
      toast({
        title: 'Bank account connected',
        description: 'Your NAB account has been linked successfully'
      });
    }, 2000);
  };

  const handleDeposit = () => {
    if (!depositAmount || depositAmount <= 0) {
      toast({
        title: 'Invalid amount',
        description: 'Please enter a valid deposit amount',
        variant: 'destructive'
      });
      return;
    }

    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setShowDepositModal(false);
      toast({
        title: 'Deposit initiated',
        description: `$${parseInt(depositAmount).toLocaleString()} will be credited to your Trove account within 1-2 business days`
      });
      setDepositAmount('');
    }, 1500);
  };

  const handleWithdraw = () => {
    if (!withdrawAmount || withdrawAmount <= 0) {
      toast({
        title: 'Invalid amount',
        description: 'Please enter a valid withdrawal amount',
        variant: 'destructive'
      });
      return;
    }

    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setShowWithdrawModal(false);
      toast({
        title: 'Withdrawal initiated',
        description: `$${parseInt(withdrawAmount).toLocaleString()} will be transferred to your bank account within 2-3 business days`
      });
      setWithdrawAmount('');
    }, 1500);
  };

  return (
    <div className="min-h-screen pt-20 pb-12 px-4 sm:px-6 lg:px-8 bg-[#F5F3ED]">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-light mb-2" style={{ fontFamily: 'Georgia, serif' }}>Banking</h1>
          <p className="text-gray-600 font-light">Manage your linked accounts and transactions</p>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Button
            onClick={() => setShowConnectModal(true)}
            className="bg-[#1a1a3e] hover:bg-[#252550] py-6 rounded-full font-light"
          >
            <Plus className="mr-2" size={20} />
            Connect Bank Account
          </Button>
          <Button
            onClick={() => setShowDepositModal(true)}
            className="bg-[#FF8C42] hover:bg-[#FFB366] text-white py-6 rounded-full font-light"
          >
            <ArrowDownToLine className="mr-2" size={20} />
            Deposit Funds
          </Button>
          <Button
            onClick={() => setShowWithdrawModal(true)}
            variant="outline"
            className="py-6 rounded-full font-light border-2"
          >
            <ArrowUpFromLine className="mr-2" size={20} />
            Withdraw Funds
          </Button>
        </div>

        {/* Connected Accounts */}
        <Card className="p-8 bg-white border-0 shadow-lg">
          <h2 className="text-2xl font-light mb-6" style={{ fontFamily: 'Georgia, serif' }}>Connected Accounts</h2>
          <div className="space-y-4">
            {connectedAccounts.map((account) => (
              <div key={account.id} className="flex items-center justify-between p-6 bg-[#F5F3ED] rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#FF8C42] rounded-full flex items-center justify-center">
                    <Building2 className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold">{account.bankName}</h3>
                    <p className="text-sm text-gray-600">{account.accountName} {account.accountNumber}</p>
                  </div>
                </div>
                {account.isPrimary && (
                  <span className="px-3 py-1 bg-[#FF8C42] text-white text-xs font-medium rounded-full">
                    Primary
                  </span>
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* Recent Transactions */}
        <Card className="p-8 bg-white border-0 shadow-lg mt-6">
          <h2 className="text-2xl font-light mb-6" style={{ fontFamily: 'Georgia, serif' }}>Recent Transactions</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <p className="font-semibold">Deposit</p>
                <p className="text-sm text-gray-600">14 Jan 2025</p>
              </div>
              <span className="text-green-600 font-semibold">+$5,000</span>
            </div>
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <p className="font-semibold">Deposit</p>
                <p className="text-sm text-gray-600">07 Jan 2025</p>
              </div>
              <span className="text-green-600 font-semibold">+$10,000</span>
            </div>
            <div className="flex items-center justify-between p-4">
              <div>
                <p className="font-semibold">Initial Deposit</p>
                <p className="text-sm text-gray-600">01 Jan 2025</p>
              </div>
              <span className="text-green-600 font-semibold">+$50,000</span>
            </div>
          </div>
        </Card>

        {/* Connect Bank Modal */}
        {showConnectModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-md w-full p-8 bg-white">
              <h3 className="text-2xl font-light mb-4" style={{ fontFamily: 'Georgia, serif' }}>Connect Bank Account</h3>
              <p className="text-gray-600 font-light mb-6">
                We use bank-level security to connect your account. Your credentials are never stored.
              </p>
              <div className="space-y-4 mb-6">
                <div className="p-4 border-2 border-gray-200 rounded-lg hover:border-[#FF8C42] cursor-pointer transition-colors">
                  <div className="flex items-center gap-3">
                    <Building2 size={24} />
                    <span className="font-semibold">Commonwealth Bank</span>
                  </div>
                </div>
                <div className="p-4 border-2 border-gray-200 rounded-lg hover:border-[#FF8C42] cursor-pointer transition-colors">
                  <div className="flex items-center gap-3">
                    <Building2 size={24} />
                    <span className="font-semibold">NAB</span>
                  </div>
                </div>
                <div className="p-4 border-2 border-gray-200 rounded-lg hover:border-[#FF8C42] cursor-pointer transition-colors">
                  <div className="flex items-center gap-3">
                    <Building2 size={24} />
                    <span className="font-semibold">ANZ</span>
                  </div>
                </div>
                <div className="p-4 border-2 border-gray-200 rounded-lg hover:border-[#FF8C42] cursor-pointer transition-colors">
                  <div className="flex items-center gap-3">
                    <Building2 size={24} />
                    <span className="font-semibold">Westpac</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowConnectModal(false)}
                  variant="outline"
                  className="flex-1 rounded-full"
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConnectBank}
                  className="flex-1 bg-[#1a1a3e] hover:bg-[#252550] rounded-full"
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={16} />
                      Connecting...
                    </>
                  ) : (
                    'Connect'
                  )}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Deposit Modal */}
        {showDepositModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-md w-full p-8 bg-white">
              <h3 className="text-2xl font-light mb-4" style={{ fontFamily: 'Georgia, serif' }}>Deposit Funds</h3>
              <p className="text-gray-600 font-light mb-6">
                Transfer funds from your linked bank account to your Trove investment account.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-light mb-2">From Account</label>
                <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF8C42]">
                  {connectedAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.bankName} - {account.accountNumber}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-light mb-2">Amount</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                    className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF8C42]"
                    placeholder="5000"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">Minimum deposit: $500</p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => setShowDepositModal(false)}
                  variant="outline"
                  className="flex-1 rounded-full"
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeposit}
                  className="flex-1 bg-[#FF8C42] hover:bg-[#FFB366] text-white rounded-full"
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={16} />
                      Processing...
                    </>
                  ) : (
                    'Deposit'
                  )}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Withdraw Modal */}
        {showWithdrawModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-md w-full p-8 bg-white">
              <h3 className="text-2xl font-light mb-4" style={{ fontFamily: 'Georgia, serif' }}>Withdraw Funds</h3>
              <p className="text-gray-600 font-light mb-6">
                Withdraw funds from your Trove account to your linked bank account.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-light mb-2">To Account</label>
                <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF8C42]">
                  {connectedAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.bankName} - {account.accountNumber}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-light mb-2">Amount</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF8C42]"
                    placeholder="1000"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">Available balance: $125,750</p>
              </div>

              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-xs text-yellow-800">
                  <strong>Note:</strong> Withdrawals from portfolios containing unlisted assets may be subject to quarterly liquidity windows.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => setShowWithdrawModal(false)}
                  variant="outline"
                  className="flex-1 rounded-full"
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleWithdraw}
                  className="flex-1 bg-[#1a1a3e] hover:bg-[#252550] rounded-full"
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={16} />
                      Processing...
                    </>
                  ) : (
                    'Withdraw'
                  )}
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default Banking;
